export type ClaudeEvent =
    | ClaudeSystemInitEvent
    | ClaudeSystemStatusEvent
    | ClaudeSystemCompactBoundaryEvent
    | ClaudeSystemHookStartedEvent
    | ClaudeSystemHookProgressEvent
    | ClaudeSystemHookResponseEvent
    | ClaudeSystemTaskStartedEvent
    | ClaudeSystemTaskNotificationEvent
    | ClaudeSystemTaskProgressEvent
    | ClaudeSystemTaskUpdatedEvent
    | ClaudeSystemApiRetryEvent
    | ClaudeSystemFilesPersistedEvent
    | ClaudeAssistantEvent
    | ClaudeUserEvent
    | ClaudeResultEvent
    | ClaudeToolProgressEvent
    | ClaudeToolUseSummaryEvent
    | ClaudeWebSearchEvent
    | ClaudeRateLimitEvent
    | ClaudeAuthStatusEvent
    | ClaudeTitleMetadataEvent
    | ClaudeRelocatedEvent
    | ClaudeWorktreeStateEvent
    | ClaudeRawJsonEvent

export interface ClaudeSystemInitEvent {
    type: "system"
    subtype: "init"
    model: string
    tools: string[]
    mcp_servers: { name: string; status: string }[]
    session_id: string
    slash_commands: string[]
    skills: string[]
    plugins: { name: string; path: string }[]
}

export interface ClaudeSystemStatusEvent {
    type: "system"
    subtype: "status"
    status: string | null
}

export interface ClaudeSystemCompactBoundaryEvent {
    type: "system"
    subtype: "compact_boundary"
    compact_metadata: { trigger: string; pre_tokens: number }
}

export interface ClaudeSystemHookStartedEvent {
    type: "system"
    subtype: "hook_started"
    hook_name: string
    hook_event: string
}

export interface ClaudeSystemHookProgressEvent {
    type: "system"
    subtype: "hook_progress"
    hook_name: string
    content: string
}

export interface ClaudeSystemHookResponseEvent {
    type: "system"
    subtype: "hook_response"
    hook_name: string
    hook_event: string
    outcome: string
}

export interface ClaudeSystemTaskStartedEvent {
    type: "system"
    subtype: "task_started"
    task_id?: string
    tool_use_id?: string
    description?: string
    task_type?: string
    uuid?: string
    session_id?: string
    [key: string]: unknown
}

export interface ClaudeSystemTaskNotificationEvent {
    type: "system"
    subtype: "task_notification"
    task_id?: string
    tool_use_id?: string
    status?: string
    output_file?: string
    summary?: string
    uuid?: string
    session_id?: string
    [key: string]: unknown
}

export interface ClaudeSystemTaskProgressEvent {
    type: "system"
    subtype: "task_progress"
    task_id?: string
    tool_use_id?: string
    description?: string
    usage?: {
        total_tokens?: number
        tool_uses?: number
        duration_ms?: number
        [key: string]: unknown
    }
    last_tool_name?: string
    uuid?: string
    session_id?: string
    [key: string]: unknown
}

export interface ClaudeSystemTaskUpdatedEvent {
    type: "system"
    subtype: "task_updated"
    task_id?: string
    patch?: {
        status?: string
        start_time?: number
        end_time?: number
        [key: string]: unknown
    }
    uuid?: string
    session_id?: string
    [key: string]: unknown
}

export interface ClaudeSystemApiRetryEvent {
    type: "system"
    subtype: "api_retry"
    attempt?: number
    max_retries?: number
    retry_delay_ms?: number
    error_status?: number
    error?: string
    session_id?: string
    uuid?: string
    [key: string]: unknown
}

export interface ClaudeSystemFilesPersistedEvent {
    type: "system"
    subtype: "files_persisted"
    [key: string]: unknown
}

export interface ClaudeAssistantEvent {
    type: "assistant"
    message: {
        id: string
        type: "message"
        role: "assistant"
        content: ClaudeContentBlock[]
        model: string
        stop_reason: string | null
        usage: {
            input_tokens: number
            output_tokens: number
            cache_creation_input_tokens?: number
            cache_read_input_tokens?: number
        }
    }
    uuid: string
    session_id: string
    parent_tool_use_id: string | null
}

export type ClaudeContentBlock =
    | { type: "text"; text: string }
    | { type: "thinking"; thinking: string; signature?: string }
    | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }

export interface ClaudeUserEvent {
    type: "user"
    message: {
        role: "user"
        content: ClaudeUserContentBlock[]
    }
    parent_tool_use_id?: string
}

export type ClaudeUserContentBlock =
    | { type: "tool_result"; tool_use_id: string; content: string | unknown[]; is_error?: boolean }
    | { type: "text"; text: string }
    | { type: "image"; source: unknown }
    | { type: "document"; source: unknown }

export interface ClaudeTitleMetadataEvent {
    type: "ai-title" | "custom-title"
    aiTitle?: string
    customTitle?: string
    sessionId?: string
}

export interface ClaudeResultEvent {
    type: "result"
    subtype: "success" | "error_during_execution" | "error_max_turns" | "error_tool_use" | "error_max_budget_usd" | "error_max_structured_output_retries"
    is_error: boolean
    result?: string
    errors?: string[]
    duration_ms: number
    duration_api_ms: number
    total_cost_usd: number
    num_turns: number
    session_id: string
    usage: Record<string, unknown>
    structured_output?: unknown
}

export interface ClaudeToolProgressEvent {
    type: "tool_progress"
    tool_use_id: string
    [key: string]: unknown
}

export interface ClaudeToolUseSummaryEvent {
    type: "tool_use_summary"
    [key: string]: unknown
}

export interface ClaudeWebSearchEvent {
    type: "web_search"
    id?: string
    query?: string
    action?: {
        type?: string
        query?: string
        queries?: string[]
        [key: string]: unknown
    }
    [key: string]: unknown
}

export interface ClaudeRateLimitEvent {
    type: "rate_limit_event"
    rate_limit_info?: {
        status?: string
        resetsAt?: number
        rateLimitType?: string
        overageStatus?: string
        overageResetsAt?: number
        isUsingOverage?: boolean
        [key: string]: unknown
    }
    uuid?: string
    session_id?: string
    [key: string]: unknown
}

export interface ClaudeAuthStatusEvent {
    type: "auth_status"
    [key: string]: unknown
}

export interface ClaudeRelocatedEvent {
    type: "relocated"
    relocatedCwd?: string
    sessionId?: string
    [key: string]: unknown
}

export interface ClaudeWorktreeSession {
    originalCwd?: string
    preEnterOriginalCwd?: string
    worktreePath?: string
    worktreeName?: string
    worktreeBranch?: string
    originalBranch?: string
    originalHeadCommit?: string
    sessionId?: string
    enteredExisting?: boolean
    [key: string]: unknown
}

export interface ClaudeWorktreeStateEvent {
    type: "worktree-state"
    worktreeSession?: ClaudeWorktreeSession | null
    sessionId?: string
    [key: string]: unknown
}

export interface ClaudeRawJsonEvent {
    type: "raw_json"
    original_type?: string
    original_subtype?: string
    raw: Record<string, unknown>
}

const KNOWN_SYSTEM_SUBTYPES = new Set<string>([
    "init",
    "status",
    "compact_boundary",
    "hook_started",
    "hook_progress",
    "hook_response",
    "local_command_output",
    "plugin_install",
    "task_started",
    "task_notification",
    "task_progress",
    "task_updated",
    "api_retry",
    "session_state_changed",
    "notification",
    "files_persisted",
    "memory_recall",
    "elicitation_complete",
    "permission_denied",
    "mirror_error",
])

const KNOWN_TOP_TYPES = new Set<string>([
    "assistant",
    "user",
    "result",
    "system",
    "tool_progress",
    "tool_use_summary",
    "rate_limit_event",
    "auth_status",
    "prompt_suggestion",
    "web_search",
    "ai-title",
    "custom-title",
    "relocated",
    "worktree-state",
])

export function parseClaudeEvent(json: unknown): ClaudeEvent | null {
    if (!json || typeof json !== "object" || Array.isArray(json)) return null

    const obj = json as Record<string, unknown>
    const type = typeof obj.type === "string" ? obj.type : undefined

    if (!type || !KNOWN_TOP_TYPES.has(type)) {
        return type
            ? {
                  type: "raw_json",
                  original_type: type,
                  raw: obj,
              }
            : null
    }

    if (type === "system") {
        const subtype = typeof obj.subtype === "string" ? obj.subtype : undefined
        if (!subtype || !KNOWN_SYSTEM_SUBTYPES.has(subtype)) {
            return {
                type: "raw_json",
                original_type: type,
                original_subtype: subtype,
                raw: obj,
            }
        }
        return obj as unknown as ClaudeEvent
    }

    return obj as unknown as ClaudeEvent
}

export function normalizeClaudeProviderEvents(messages: readonly unknown[]): ClaudeEvent[] {
    return normalizeClaudeProviderEventsWithSources(messages).events
}

export function normalizeClaudeProviderEventsWithSources(messages: readonly unknown[]): { events: ClaudeEvent[]; sourceIndices: number[] } {
    const events: ClaudeEvent[] = []
    const sourceIndices: number[] = []
    messages.forEach((message, sourceIndex) => {
        const event = parseClaudeProviderEvent(message)
        if (!event) return
        events.push(event)
        sourceIndices.push(sourceIndex)
    })
    return { events, sourceIndices }
}

export function parseClaudeProviderEvent(message: unknown): ClaudeEvent | null {
    if (isAlreadyNormalizedClaudeRawJsonEvent(message)) return message
    return parseClaudeEvent(message)
}

export function claudeWorkingDirectoryFromEvent(message: unknown): string | null {
    const event = parseClaudeProviderEvent(message)
    if (!event) return null

    let path: string | undefined
    switch (event.type) {
        case "relocated":
            path = event.relocatedCwd
            break
        case "worktree-state":
            path = event.worktreeSession?.worktreePath
            break
        default:
            return null
    }
    if (typeof path !== "string") return null
    return path.trim() || null
}

function isAlreadyNormalizedClaudeRawJsonEvent(message: unknown): message is ClaudeEvent {
    return (
        typeof message === "object" &&
        message !== null &&
        !Array.isArray(message) &&
        (message as { type?: unknown }).type === "raw_json" &&
        typeof (message as { raw?: unknown }).raw === "object" &&
        (message as { raw?: unknown }).raw !== null
    )
}
